import { isBefore } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import Button from '../../components/basics/Button/Button'
import ExpandedIndicator from '../../components/basics/ExpandedIndicator/ExpandedIndicator'
import ClearTextInput from '../../components/basics/TextInput/ClearTextInput'
import TextInput from '../../components/basics/TextInput/TextInput'
import Pagination from '../../components/Pagination/Pagination'
import UserSlugs from '../../components/slugs/UserSlugs'
import UserAvatar from '../../components/UserAvatar/UserAvatar'
import { ScheduledCourseEventStatus } from '../../models/BaseModels'
import { ScheduledCourse } from '../../models/LearningItem'
import { User } from '../../models/User'
import bookingForLearningItem from '../../state/calculations/bookingForLearningItem'
import {
  userCanAccessEventDateAttendeesList,
  userCanBookLearningItem,
  userCanBookLearningItemAfterItHasStarted,
  userCanCancelBooking,
  managerCanCancelBookingOfGroupMember,
  managerCanCancelBookingOfGroupMemberWhenAssignmentWasAddedDirectly,
} from '../../state/calculations/permissions/userCan'
import scheduledCourseParticipants from '../../state/calculations/scheduledCourseParticipants'
import useBookScheduledCourseEvent from '../../state/mutations/useBookScheduledCourseEvent'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useSearchableList from '../../utilities/useSearchableList'

import styles from './CoursePage.module.scss'
import GroupsIcon from '@material-symbols/svg-300/rounded/groups.svg'
import PersonOffIcon from '@material-symbols/svg-300/outlined/person_off.svg'
import BeenHereIcon from '@material-symbols/svg-300/outlined/beenhere.svg'
import NotListedLocationIcon from '@material-symbols/svg-300/outlined/not_listed_location.svg'
import { isOneOfUsers } from '../../state/calculations/usersHelpers'
import { getAttendeeStatus } from '../../state/calculations/attendeesHelpers'
import useDialog from '../../utilities/useDialog'
import useCancelScheduledCourseEventBooking from '../../state/mutations/useCancelScheduledCourseEventBooking'
import { ConfirmCancelBookingDialog } from '../ManageUserPage/ManageUserPageLearningAssignments'
import useInvalidate from '../../state/useInvalidate'
import useParticipantsOtherGroupAssignmentsThanManagerGroups from '../../state/queries/useParticipantsOtherGroupAssignmentsThanManagerGroups'

/**********************************************************************
 * CoursePageScheduledCourseQuickfacts
 **********************************************************************/
export const CoursePageScheduledCourseQuickfacts = React.memo(function CoursePageScheduledCourseQuickfacts({
  scheduledCourse,
}: {
  scheduledCourse: ScheduledCourse
}) {
  const { t } = useTranslation()

  return (
    <aside className={styles.CoursePageScheduledCourseQuickfacts}>
      <h2>{t('learning item.quickFacts')}</h2>

      <dl>
        {scheduledCourse.categories.length > 0 && (
          <>
            <dt>{t('learning item.categories')}</dt>
            <dd>{scheduledCourse.categories.map((category) => category.name).join(', ')}</dd>
          </>
        )}

        {scheduledCourse.provider && (
          <>
            <dt>{t('learning item.field provider')}</dt>
            <dd>{scheduledCourse.provider.name}</dd>
          </>
        )}

        <dt>{t('learning item.price')}</dt>
        <dd>{t('learning item.included')}</dd>
      </dl>
    </aside>
  )
})

/**********************************************************************
 * CoursePageScheduledCourseEvents
 **********************************************************************/
export const CoursePageScheduledCourseEvents = React.memo(function CoursePageScheduledCourseEvents({
  scheduledCourse,
}: {
  scheduledCourse: ScheduledCourse
}) {
  const { t } = useTranslation()

  return (
    <section className={styles.CoursePageScheduledCourseEvents}>
      <h1>{t('learning item.scheduled course events')}</h1>

      <main>
        <CoursePageScheduledCourseEventsList scheduledCourse={scheduledCourse} fromPath={false} />
      </main>
    </section>
  )
})

/**
 * It is used in CourseLearningPathPage as well.
 */
export const CoursePageScheduledCourseEventsList = React.memo(function CoursePageScheduledCourseEventsList({
  scheduledCourse,
  fromPath,
}: {
  scheduledCourse: ScheduledCourse
  fromPath: boolean
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const invalidate = useInvalidate()
  const { user: currentUser } = useSession()

  const booking = useMemo(() => bookingForLearningItem(scheduledCourse), [scheduledCourse])

  const canBook = userCanBookLearningItem(currentUser, scheduledCourse)

  // book event
  const bookScheduledCourseEvent = useBookScheduledCourseEvent()
  const handleBookEvent = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      const eventUuid = e.currentTarget.dataset.eventUuid
      if (eventUuid && currentUser) {
        bookScheduledCourseEvent.mutate({ eventUuid, userUuid: currentUser.uuid })
      }

      if (fromPath) {
        invalidate.pathLearningItems()
      }
    },
    [currentUser, scheduledCourse, bookScheduledCourseEvent]
  )

  const now = new Date()

  const canBookAfterItHasStarted = userCanBookLearningItemAfterItHasStarted(currentUser, scheduledCourse)

  return (
    <div className={styles.CoursePageScheduledCourseEventsList}>
      {scheduledCourse.scheduledCourse.events.map((event) => {
        const isCurrentUserParticipant = isOneOfUsers(currentUser?.uuid ?? '', event.participants)

        return (
          <article key={event.uuid}>
            <details open={scheduledCourse.scheduledCourse.events.length === 1 ? true : undefined}>
              <summary>
                <ExpandedIndicator horizontal />
                <header>
                  <h2>{t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}</h2>{' '}
                  <span>
                    (
                    {[
                      event.dates.length > 1 && `${t('learning item.{count} dates', { count: event.dates.length })}`,
                      t('learning item.{count} scheduled course event seats available', {
                        count: event.seatsAvailable,
                      }),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    )
                  </span>
                </header>
                {booking ? (
                  event.uuid === booking.scheduledCourseEventUuid && (
                    <span className={styles.booked}>{t('learning item.booked')}</span>
                  )
                ) : canBook ? (
                  <Button
                    small
                    data-event-uuid={event.uuid}
                    onClick={handleBookEvent}
                    disabled={
                      (isBefore(event.startDate, now) && !canBookAfterItHasStarted) ||
                      event.status !== ScheduledCourseEventStatus.Bookable
                    }
                  >
                    {t('learning item.book')}
                  </Button>
                ) : null}
              </summary>
              <table className={styles.dateTable}>
                <thead>
                  <tr>
                    <th className={styles.dateColumn}>{t('learning item.field scheduled course date date')}</th>
                    <th className={styles.titleColumn}>{t('learning item.field scheduled course date title')}</th>
                    <th className={styles.trainerColumn}>{t('learning item.field scheduled course date trainers')}</th>
                    <th className={styles.locationColumn}>{t('learning item.scheduled course date location')}</th>
                    <th className={styles.participantsColumn} />
                  </tr>
                </thead>
                <tbody>
                  {event.dates.map((date) => {
                    let attendeeStatus = null

                    const canAccessAttendeesList = userCanAccessEventDateAttendeesList(currentUser!, date.trainers!)

                    if (date.attendees) {
                      attendeeStatus = getAttendeeStatus(currentUser?.uuid ?? '', date.attendees)
                    }

                    return (
                      <tr key={date.uuid}>
                        <td className={styles.dateColumn}>
                          {t('format.DATETIMERANGE', { range: { start: date.startDate, end: date.endDate } })}
                        </td>

                        <td className={styles.titleColumn}>{date.title}</td>

                        <td className={styles.trainerColumn}>
                          <UserSlugs max={2} uuids={date.trainers?.map((trainer) => trainer.uuid) || []} />
                        </td>

                        <td className={styles.locationColumn}>
                          {date.meetingAddress && <span>{date.meetingAddress}</span>}
                          {date.meetingLink && <span>{date.meetingLink}</span>}
                          {date.meetingRoom && <span>{date.meetingRoom}</span>}
                        </td>

                        <td className={styles.participantsColumn}>
                          {event.participants.length > 0 && canAccessAttendeesList && (
                            <Button
                              className={styles.participantsButton}
                              small
                              component={Link}
                              data-event-uuid={event.uuid}
                              to={routes.manageCourseEventDateAttendees.url({
                                uuid: scheduledCourse.uuid!,
                                eventUuid: event.uuid!,
                                dateUuid: date.uuid!,
                                fromPath: fromPath ? '1' : null,
                              })}
                            >
                              <GroupsIcon />
                            </Button>
                          )}

                          {event.participants.length > 0 && isCurrentUserParticipant && !canAccessAttendeesList && (
                            <div>
                              {attendeeStatus === null ? (
                                <div className={styles.unknownAttendanceIcon}>
                                  <NotListedLocationIcon />
                                </div>
                              ) : attendeeStatus === false ? (
                                <div className={styles.notAttendedIcon}>
                                  <PersonOffIcon />
                                </div>
                              ) : (
                                <div className={styles.attendedIcon}>
                                  <BeenHereIcon />
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </details>
          </article>
        )
      })}
    </div>
  )
})

const getBookedEventUuid = (userUuid: string, scheduledCourse: ScheduledCourse): string => {
  return scheduledCourse.scheduledCourse.events.find((event) => {
    return event.participants.find((participant) => {
      if (participant.uuid === userUuid) {
        return event
      }
    })
  })!.uuid
}

/**********************************************************************
 * CoursePageScheduledCourseParticipants
 **********************************************************************/
const participantListInit = {
  prefix: 'participants',
  pageSize: 15,
  filter:
    (search: string) =>
    ({ participant: { firstname, lastname } }: { participant: User }) =>
      `${firstname} ${lastname}`.toLowerCase().includes(search.toLowerCase()),
}
export const CoursePageScheduledCourseParticipants = React.memo(function CoursePageScheduledCourseParticipants({
  scheduledCourse,
}: {
  scheduledCourse: ScheduledCourse
}) {
  const { t } = useTranslation()
  const routes = useRoutes()

  const participants = useMemo(() => scheduledCourseParticipants(scheduledCourse), [scheduledCourse])

  const participantList = useSearchableList(participantListInit, participants)
  const updateSearch = useDebouncedCallback(participantList.setSearch, 300)
  const [search, setSearch] = useState(participantList.search)
  const handleSearchChanged = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.currentTarget.value)
      updateSearch(e.currentTarget.value)
    },
    [setSearch, updateSearch]
  )

  const showDialog = useDialog()

  const { user: currentUser } = useSession()

  const { participantsWithOtherAssignments } = useParticipantsOtherGroupAssignmentsThanManagerGroups(
    scheduledCourse.uuid,
    participants.map(({ participant }) => participant.uuid),
    currentUser?.uuid || '',
    // Admin or CSM always can cancel booking so we
    // do not need to run this query for them.
    !!currentUser?.role && currentUser?.role !== 'admin' && currentUser?.role !== 'customer_success_manager'
  )

  const cancelScheduledCourseEventBooking = useCancelScheduledCourseEventBooking()

  const invalidate = useInvalidate()

  const handleCancelBooking = useCallback(
    async (userUuid: string) => {
      const confirmed = await showDialog(
        (close) => (
          <ConfirmCancelBookingDialog close={close} userUuid={userUuid} learningItemUuid={scheduledCourse.uuid} />
        ),
        () => false
      )

      if (confirmed) {
        const bookedEventUuid = getBookedEventUuid(userUuid, scheduledCourse)

        cancelScheduledCourseEventBooking.mutate({ eventUuid: bookedEventUuid, userUuid })

        // When a logged-in user, who is performing the cancellation action, is not assigned to this
        // course or booked to its events, then we need to invalid the course collection.
        // So, it is easier to always invalidate rather than checking the condition.
        invalidate.courses()
      }
    },
    [cancelScheduledCourseEventBooking]
  )

  return (
    <section className={styles.CoursePageScheduledCourseParticipants}>
      <h1>{t('learning item.scheduled course participants')}</h1>

      <div className={styles.searchbar}>
        <TextInput
          name="search"
          label={t('search users')}
          type="text"
          autoComplete="off"
          omitErrorDisplay
          value={search || ''}
          onChange={handleSearchChanged}
          after={<ClearTextInput />}
        />
      </div>

      <main>
        <table className={styles.participantsTable}>
          <thead>
            <tr>
              <th className={styles.avatarColumn}></th>
              <th className={styles.nameColumn}>{t('user.field name')}</th>
              <th className={styles.eventsColumn}>{t('learning item.scheduled course events')}</th>
              <th className={styles.buttonsColumn}></th>
            </tr>
          </thead>
          <tbody>
            {participantList.items.map(({ participant, events }) => {
              const participantAssignmentData = participantsWithOtherAssignments.find(
                ({ participantUuid }) => participantUuid === participant.uuid
              )

              return (
                <tr key={participant.uuid}>
                  <td className={styles.avatarColumn}>
                    <UserAvatar link user={participant} />
                  </td>

                  <td className={styles.nameColumn}>
                    <Link to={routes.manageUser.url({ uuid: participant.uuid })}>
                      {participant.firstname} {participant.lastname}
                    </Link>
                  </td>

                  <td className={styles.eventsColumn}>
                    {events.map((event) => (
                      <span key={event.uuid}>
                        {t('format.DATERANGE', { range: { start: event.startDate, end: event.endDate } })}
                      </span>
                    ))}
                  </td>

                  <td className={styles.buttonsColumn}>
                    {(userCanCancelBooking(currentUser) ||
                      (participantAssignmentData &&
                        participantAssignmentData.finishedAt === null &&
                        !participantAssignmentData.hasOtherGroupsAssignmentsThanFromProvidedManager &&
                        (managerCanCancelBookingOfGroupMember(
                          currentUser?.managedGroups || [],
                          participant.groups || [],
                          scheduledCourse.groupLearningAssignments || []
                        ) ||
                          managerCanCancelBookingOfGroupMemberWhenAssignmentWasAddedDirectly(
                            currentUser?.managedGroups || [],
                            participant.groups || [],
                            participantAssignmentData.hasDirectAssignmentFromManager
                          )))) && (
                      <Button filled small onClick={() => handleCancelBooking(participant.uuid)}>
                        {t('learning item.cta cancel booking')}
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        <Pagination
          page={participantList.page}
          pageCount={participantList.pageCount}
          pageSize={participantList.pageSize}
          itemCount={participantList.itemCount}
          onPageChanged={participantList.setPage}
        />
      </main>
    </section>
  )
})
